/*------------------------------------------------------------------------------
   About      : Content & Logic form section (epic DA-697, II-FE-3)

                Mockups: `image-1.png` — a top row (measurement / appliance /
                type / validity) over a collapsible "Extended classification
                details" panel.

                **Field visibility and requiredness come from the catalog, not
                from hardcoded per-type rules.** `types[].requires` says which of
                pickUnit / compareUnit / gap / direction apply, and
                `benchmarkPercentile` says whether that field exists for the
                type. Hardcoding this would drift the moment quill adds a type.

                On an existing record the classification fields are READ-ONLY:
                they feed `code` derivation, and `code` is immutable for life
                (pingpong joins on it as a Cassandra clustering column).

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Grid,
         MenuItem, TextField, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { InsightCatalog, InsightCreateData } from '../types/insightTypes'
import { DIRECTIONS,
         DURATION_TYPES,
         INSIGHT_CATEGORIES,
         MEASUREMENT_TYPES,
         SENTIMENTS,
         TIME_OF_USE,
         VALIDITY_TYPES }                    from '../constants/insightClassification'
import { applianceOptions,
         groupSupportsDerivation,
         typeDefFor }                        from '../utils/insightCatalogHelpers'
import { EditableInsightField }              from '../utils/insightDiff'
import { FIELD_LABEL_SX,
         SECTION_HEADING_SX }                from './insightFormStyles'

interface InsightClassificationFormProps {
  value        : InsightCreateData
  onChange     : (patch: Partial<InsightCreateData>) => void
  catalog     ?: InsightCatalog
  /** True on an existing record — classification feeds immutable `code`. */
  readOnly    ?: boolean
  modified     : Set<EditableInsightField>
}

export const InsightClassificationForm = ({
  value,
  onChange,
  catalog,
  readOnly,
  modified,
}: InsightClassificationFormProps) => {

  const typeDef  = typeDefFor(catalog, value.type)
  const requires = typeDef?.requires

  // Green highlight for a field that differs from the published version, matching
  // `getModifiedFieldStyle` in RecoEditFormComponent.
  // Every field also carries FIELD_LABEL_SX so its resting label is primary
  // text rather than the MUI default grey.
  const modifiedSx = (field: EditableInsightField) => ({
    ...FIELD_LABEL_SX,
    ...(modified.has(field) ? {
      '& .MuiInputBase-root' : {
        bgcolor : (theme: any) => theme.palette.mode === 'light'
          ? 'rgba(196, 255, 176, 0.5)'
          : 'rgba(98, 128, 88, 0.5)',
      },
    } : undefined),
  })

  const select = (
    field   : EditableInsightField,
    label   : string,
    options : { value : string; label : string }[],
    required = false,
  ) => (
    <TextField
      select fullWidth size="small" label={label} required={required}
      disabled={readOnly}
      value={(value as any)[field] ?? ''}
      onChange={(e) => onChange({ [field] : e.target.value || null } as Partial<InsightCreateData>)}
      sx={modifiedSx(field)}
    >
      <MenuItem value="">—</MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </TextField>
  )

  const numberField = (
    field       : EditableInsightField,
    label       : string,
    required    = false,
    helperText ?: string,
  ) => (
    <TextField
      fullWidth size="small" type="number" label={label} required={required}
      disabled={readOnly}
      value={(value as any)[field] ?? ''}
      helperText={helperText}
      onChange={(e) => onChange({
        // '' must become null, not NaN — NaN serialises to null anyway but
        // fails the diff's absence check and reads as a change every save.
        [field] : e.target.value === '' ? null : Number(e.target.value),
      } as Partial<InsightCreateData>)}
      sx={modifiedSx(field)}
    />
  )

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ ...SECTION_HEADING_SX, mb : 1.5 }}>
        Content &amp; logic
      </Typography>

      {/* The reason lives here rather than on screen: these fields derive the
          insight's `code`, which pingpong joins on as a Cassandra clustering
          column and which cannot change for the life of the record. An author
          cannot act on that, so the banner states the rule and stops. */}
      {readOnly && (
        <Alert severity="info" sx={{ mb : 2 }}>
          Classification is read-only after creation.
        </Alert>
      )}

      {/* ── Primary row ─────────────────────────────────────────────────── */}
      <Grid container spacing={2}>
        <Grid size={{ xs : 12, sm : 6, md : 3 }}>
          {select('measurementType', 'Measurement', MEASUREMENT_TYPES, true)}
        </Grid>

        <Grid size={{ xs : 12, sm : 6, md : 3 }}>
          <TextField
            select fullWidth size="small" label="Appliance" required
            disabled={readOnly}
            value={value.applianceId ?? ''}
            onChange={(e) => onChange({
              applianceId : e.target.value === '' ? null : Number(e.target.value),
            })}
            sx={modifiedSx('applianceId')}
          >
            <MenuItem value="">—</MenuItem>
            {applianceOptions(catalog).map((appliance) => (
              <MenuItem key={appliance.applianceId} value={appliance.applianceId}>
                {appliance.displayName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs : 12, sm : 6, md : 3 }}>
          <TextField
            select fullWidth size="small" label="Type" required
            disabled={readOnly}
            value={value.type ?? ''}
            onChange={(e) => {
              // Changing type changes which fields apply, so clear the ones the
              // new type does not use. Leaving them set would send values the
              // server has no slot for and that the user cannot see or correct.
              const nextType  = e.target.value
              const nextDef   = typeDefFor(catalog, nextType)
              const nextNeeds = nextDef?.requires
              onChange({
                type                : nextType,
                ...(nextNeeds?.pickUnit    ? {} : { pickUnitType : null, pickUnitCount : null }),
                ...(nextNeeds?.compareUnit ? {} : { compareUnitType : null, compareUnitCount : null }),
                ...(nextNeeds?.gap         ? {} : { gap : null }),
                ...(nextNeeds?.direction   ? {} : { direction : null }),
                ...(nextDef?.benchmarkPercentile ? {} : { benchmarkPercentile : null }),
              })
            }}
            sx={modifiedSx('type')}
          >
            <MenuItem value="">—</MenuItem>
            {(catalog?.types ?? []).map((type) => (
              <MenuItem key={type.code} value={type.code}>{type.displayName}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs : 12, sm : 6, md : 3 }}>
          {select('validityType', 'Validity', VALIDITY_TYPES, true)}
        </Grid>
      </Grid>

      {/* ── Extended classification ─────────────────────────────────────── */}
      <Accordion defaultExpanded={false} sx={{ mt : 2, boxShadow : 'none', '&:before' : { display : 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={SECTION_HEADING_SX}>
            EXTENDED CLASSIFICATION DETAILS
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs : 12, sm : 6, md : 4 }}>
              {/* quill-fe owns this vocabulary — quill types `category` as a
                  plain string with no enum and serves no catalog for it, so the
                  list lives in `constants/insightClassification.ts` beside the
                  other six. Add values there, not here. */}
              {select('category', 'Insight category', INSIGHT_CATEGORIES)}
            </Grid>

            <Grid size={{ xs : 12, sm : 6, md : 4 }}>
              {select('timeOfUse', 'Time of use', TIME_OF_USE)}
            </Grid>

            {/* Rendered only when the catalog says this type uses it. */}
            {requires?.direction && (
              <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                {select('direction', 'Direction', DIRECTIONS, true)}
              </Grid>
            )}

            <Grid size={{ xs : 12, sm : 6, md : 4 }}>
              {/* quill: "sentimentType is required for PEER" — the only type
                  with a requiredness rule the `requires` map does not carry. */}
              {select('sentimentType', 'Sentiment', SENTIMENTS, value.type === 'PEER')}
            </Grid>

            {requires?.pickUnit && (
              <>
                <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                  {select('pickUnitType', 'Unit type', DURATION_TYPES, true)}
                </Grid>
                <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                  {numberField('pickUnitCount', 'Unit count', true)}
                </Grid>
              </>
            )}

            {requires?.compareUnit && (
              <>
                <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                  {select('compareUnitType', 'Compare type', DURATION_TYPES, true)}
                </Grid>
                <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                  {numberField('compareUnitCount', 'Compare count', true)}
                </Grid>
              </>
            )}

            {/* Gap appears exactly when the catalog says the type needs one —
                `SELF_COMPARE_WITH` is the only type with `requires.gap`, and
                quill rejects a save without it ("gap is required for …"), so it
                is marked required rather than left looking optional. */}
            {requires?.gap && (
              <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                {numberField('gap', 'Gap', true,
                  'Periods between the pick and compare windows')}
              </Grid>
            )}

            {typeDef?.benchmarkPercentile && (
              <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                {numberField('benchmarkPercentile', 'Benchmark percentile')}
              </Grid>
            )}

            <Grid size={{ xs : 12 }}>
              <TextField
                select fullWidth size="small" label="Group"
                disabled={readOnly}
                value={value.groupInfo?.group ?? ''}
                onChange={(e) => onChange({
                  // groupInfo is merged server-side, so the whole object goes.
                  groupInfo : e.target.value
                    ? { ...value.groupInfo, group : e.target.value }
                    : null,
                })}
                sx={modifiedSx('groupInfo')}
              >
                <MenuItem value="">—</MenuItem>
                {(catalog?.groups ?? []).map((group) => (
                  <MenuItem key={group.code} value={group.code}>
                    {group.displayName}
                    {!group.shortCode && ' — needs a code override'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Said up front rather than after a server rejection: quill's
                buildCode refuses a group with no established prefix, because a
                wrong prefix writes a wrong Cassandra clustering key. */}
            {value.groupInfo?.group && !groupSupportsDerivation(catalog, value.groupInfo.group) && (
              <Grid size={{ xs : 12 }}>
                <Alert severity="warning">
                  No code prefix is established for this group, so the server cannot
                  derive a <code>code</code> for it. Creating this insight needs a
                  <code> codeOverride</code>, which requires FULL_ACCESS.
                </Alert>
              </Grid>
            )}

            {value.groupInfo?.group && (
              <Grid size={{ xs : 12, sm : 6, md : 4 }}>
                <TextField
                  fullWidth size="small" type="number" label="Group weight"
                  disabled={readOnly}
                  value={value.groupInfo?.weight ?? ''}
                  onChange={(e) => onChange({
                    groupInfo : {
                      ...value.groupInfo,
                      group  : value.groupInfo?.group ?? '',
                      weight : e.target.value === '' ? null : Number(e.target.value),
                    },
                  })}
                  sx={FIELD_LABEL_SX}
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default InsightClassificationForm
